import { GLYPH_OWN_SIZE } from 'components/buttons/buttonStyle';
import { ToggleGroup, ToggleGroupItem } from 'components/ui/toggle-group';
import { ComponentProps, ReactNode } from 'react';
import { cn } from 'utils/cn';

type Props<T extends string> = {
    value: T;
    onChange: (next: T) => void;
    /** Names the control for screen readers, e.g. "Group by". */
    'aria-label': string;
    children: ReactNode;
};

/**
 * A switch that changes how the SAME list is presented — Groups/Contacts, List/Grouped, Group by.
 *
 * Not for form fields that render as segments (a multiplier code, a 1-5 score): green would read as
 * a verdict on the value.
 */
export function ViewSwitch<T extends string>({ value, onChange, children, ...rest }: Props<T>): JSX.Element {
    return (
        <ToggleGroup
            {...rest}
            variant="outline"
            spacing={0}
            value={[value]}
            // A view switch is never in no view at all, so pressing the active option again is ignored.
            onValueChange={(next) => next[0] !== undefined && onChange(next[0] as T)}
        >
            {children}
        </ToggleGroup>
    );
}

/**
 * One option of a {@link ViewSwitch}. The active one is filled with the brand green — 8.9:1 against
 * its white label, where a pale fill would fail WCAG 2.2 SC 1.4.11 — and the rest are white with a
 * grey border and label. Its glyph keeps its own size, as a button's does.
 */
export function ViewSwitchItem({ className, ...props }: ComponentProps<typeof ToggleGroupItem>) {
    return (
        <ToggleGroupItem
            className={cn(
                'border-grey-300 bg-paper text-grey-600 hover:bg-grey-100 hover:text-grey-600 h-auto gap-1.5 p-[7px] text-[0.8125rem] leading-[1.75] font-medium',
                'aria-pressed:border-primary aria-pressed:bg-primary aria-pressed:text-primary-foreground aria-pressed:hover:bg-primary aria-pressed:hover:text-primary-foreground aria-pressed:font-semibold',
                GLYPH_OWN_SIZE,
                className
            )}
            {...props}
        />
    );
}
